import React, { FC } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { createUseStyles } from 'react-jss';
import colors from '@packages/core/styles/colors';
import { Button, Typography, Icon } from '@packages/ui/shared';
import { StyleSheet } from 'react-native';
import Card from './card';
import { useNavigate } from 'react-router-dom';
import { OpenInNew } from '@web/vin-sessions/assets/Icons';
import { useAuthState } from '@packages/contexts/auth';
import { handleLogin } from '../utils';

interface MainPageProps {}

const useMainContentStyles = createUseStyles({
    mainContentContainer: {
        display: 'flex',
        flexDirection: 'column',
        borderBottom: `1px solid ${colors.grayThree}`,
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        height: '100%',
        '@media (max-width: 1200px)': {
            borderBottom: 'none',
            boxShadow: 'none',
        },
    },
    row: {
        flex: 1,
    },
    leftColContainer: {
        width: '60%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        '@media (max-width: 1200px)': {
            justifyContent: 'flex-start',
            paddingTop: 24,
            paddingBottom: 24,
        },
        '@media (max-height: 700px)': {
            justifyContent: 'flex-start',
            paddingTop: 16,
            paddingBottom: 16,
        },
    },
    rightColContainer: {
        width: '40%',
        backgroundColor: colors.grayTwo,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        '@media (max-width: 1200px)': {
            paddingTop: 16,
            paddingBottom: 24,
        },
    },
    cardContainer: {
        display: 'flex',
        gap: 16,
        flexDirection: 'column',
        width: '85%',
    },
    logo: {
        height: 40,
        width: 270,
        marginTop: 6,
    },
    textContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
    },
});

const styles = StyleSheet.create({
    welcomeText: {
        fontSize: 50,
        lineHeight: 50,
        color: colors.blueZero,
        fontWeight: '300',
    },
    noteOne: {
        lineHeight: 36,
        fontSize: 24,
        fontWeight: '400',
    },
    noteTwo: {
        fontWeight: '400',
        fontSize: 15,
        lineHeight: 20,
        color: '#4C525D',
    },
    noteThree: {
        fontWeight: '400',
        fontSize: 15,
        lineHeight: 20,
        color: '#4C525D',
    },
});

export const MainPage: FC<MainPageProps> = (props) => {
    const classes = useMainContentStyles();
    const navigate = useNavigate();
    const { showUnauthorizedPrompt, account } = useAuthState();
    const isAuthenticated = !!account;
    const pathPrefix = isAuthenticated ? '/app' : '/public';

    return (
        <Container fluid className={classes.mainContentContainer}>
            <Row className={`align-items-stretch ${classes.row}`}>
                <Col md={7} className={`p-10 ${classes.leftColContainer}`}>
                    <Container fluid style={{ maxWidth: '90%' }}>
                        <div className="d-flex">
                            <Typography style={styles.welcomeText}>{'Welcome to TechShare'}</Typography>
                        </div>
                        <div className="mt-4" style={{ maxWidth: '95%' }}>
                            <Typography style={styles.noteOne}>
                                {'Subaru Technical Support & Information Portal'}
                            </Typography>
                        </div>
                        <div className="mt-8" style={{ maxWidth: '80%' }}>
                            <div className={classes.textContainer}>
                                <Typography style={styles.noteTwo}>
                                    {'This site provides access to Subaru technical information and support resources.'}
                                </Typography>
                                <Typography style={styles.noteThree}>
                                    {'Authorized users may sign in to continue.'}
                                </Typography>
                            </div>
                            <div className="mt-10" style={{ maxWidth: '75%' }}>
                                <Button
                                    title={'Sign in'}
                                    onPress={() => handleLogin(showUnauthorizedPrompt, navigate)}
                                />
                                {/* <div className="mt-1">
                                    <Typography>{'Not a member yet?'}</Typography>
                                    <Link to={'#'} className="ml-1">
                                        {'Create account'}
                                    </Link>
                                </div> */}
                            </div>
                        </div>
                    </Container>
                </Col>

                <Col md={5} className={classes.rightColContainer}>
                    <div className={classes.cardContainer}>
                        <Card
                            title="STIS Public Documents"
                            content="Explore available public documents in STIS. Some documents require a subscription to access."
                            Icon={<Icon size={16} name="chevron-right" color="blueOne" />}
                            buttonText="Search for documents"
                            borderFlag={true}
                            onClick={() => navigate('/stis')}
                        />

                        <Card
                            title="Emergency Response Guides"
                            content="Find essential procedures for first responders to safely manage emergencies. Only for Subaru electric and hybrid cars (EVs)."
                            Icon={<Icon size={16} name="chevron-right" color="blueOne" />}
                            buttonText="View guides by model"
                            borderFlag={true}
                            onClick={() => navigate(`${pathPrefix}/emergency-guides`)}
                        />

                        <Card
                            title="Immobilizer Key"
                            content="Subaru Vehicle Security Professional (VSP) for key and immobilizer codes."
                            Icon={<OpenInNew style={{ marginBottom: 0 }} fill={colors.blueOne} />}
                            buttonText="Go to VSP"
                            borderFlag={false}
                            onClick={() => window.open('https://subaru.locksmithsdrm.com/SubaruSDRM/', '_blank')}
                        />
                    </div>
                </Col>
            </Row>
        </Container>
    );
};
